import {
  QueryClient,
  useInfiniteQuery,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient
} from "react-query";
import {
  UseInfiniteQueryOptions,
  UseQueryOptions
} from "react-query/types/react/types";

import { HttpError, HttpStatusCode, NotFoundError } from "@bps/http-client";
import { useGateways } from "@libs/api/gateways-context";
import {
  AddLicenceDto,
  AddSalesProductDto,
  AddStripeCustomerDto,
  AddSubscriptionPayload,
  BillingContactDto,
  BusinessRoleDto,
  BusinessSecurityRoleDto,
  CancelCancelCustomerSubscriptionsArgs,
  CanUnlinkDto,
  ComponentActionDto,
  ComponentActionPayload,
  ComponentDefDto,
  ComponentDto,
  ComponentLiteDto,
  ComponentRequestDto,
  ComponentSettingDto,
  ComponentSettingsDto,
  ComponentWorkflowRunInstanceDto,
  CreateComponentPayload,
  CreateSettingPayload,
  CreateTenantDto,
  CustomerProductsDto,
  Feature,
  FeatureAction,
  GetBusinessSecurityRolesParams,
  GetComponentActionsArgs,
  GetComponentArgs,
  GetComponentsArgs,
  GetComponentsDefArgs,
  GetComponentSettingArgs,
  GetComponentsRequestsArgs,
  GetFeatureParams,
  GetLicencesArgs,
  GetSalesProductsArgs,
  GetTenantFeatureParams,
  InvoiceDto,
  LicenceDto,
  LicenceTypeDto,
  NewPltUser,
  NewTenantTag,
  PaymentMethodDto,
  PaymentMethodSetupDto,
  PaymentMethodUpdateArgs,
  PltUser,
  PreviewSubscriptionPayload,
  PromotionCodeDto,
  ResetCustomerSubscriptionBillingDateArgs,
  SalesProductDto,
  SecurityRoleAssignmentsDto,
  SecurityRoleDto,
  SecurityRolesAndPermissionsDto,
  SecurityRoleSetDto,
  SourceTypeDto,
  StripeCustomerDto,
  SubscriptionDto,
  SubscriptionPreviewDto,
  Tenant,
  TenantDefaultUsersAuthenticationDto,
  TenantDto,
  TenantFeature,
  TenantFeatureDetails,
  TenantNetworkAuthenticationSettingsDto,
  TenantNetworkAuthenticationSettingsPayload,
  TenantNetworkAuthenticationSettingsQuery,
  TenantSearchArgs,
  TenantTag,
  TenantUserSearchArgs,
  TransferTenantDto,
  UnlinkTenantDto,
  UpdateSettingArgs,
  UpdateSettingPayload,
  UpdateUserRolesPayload,
  UsedTenantTag,
  UserDefaultAuthenticationDto,
  UserNetworkAuthenticationSettingsDto,
  UserNetworkAuthenticationSettingsPayload,
  UserSecPermissionDto,
  UserSecRolesDto
} from "@libs/api/gateways/plt/plt-gateway.dtos";
import { guid } from "@libs/common/guid";
import { PagingResponse } from "@libs/paging/paging-response.type";
import {
  cacheDeleteSingleItemInArray,
  cacheUpsertSingleItemInArray
} from "@libs/react-query/react-query-cache.utils";

import { useFeedbackContext } from "../../../stores/feedback-context/feedback-context";
import { RefDataDto } from "../../types/common-dtos";

export const PltCacheKeys = {
  Tenants: "plt-tenants",
  TenantTags: "plt-tenanttags",
  UsedTenantTags: "plt-usedtenanttags",
  Users: "plt-users",
  TenantFeature: "tenant-feature",
  UsersSearch: "plt-users-search",
  User: "plt-user",
  UserLogs: "plt-user-bpid-logs",
  UserDefaultAuthentication: "user-default-auth",
  UserNetworkAuthenticationSetting: "user-auth-network-setting",
  Feature: "feature",
  SecurityRolesAndPermissions: "security-roles-and-permissions",
  BusinessRoles: "business-roles",
  SecurityRolesSets: "security-role-sets",
  SecurityRoles: "security-roles",
  SecurityBusinessRoles: "security-business-roles",
  UserRoles: "plt-users-roles",
  UserPermissions: "plt-users-permissions",
  UserAssignedSecurityRoles: "plt-assigned-users-security-roles",
  TenantFeatures: "tenant-features",
  TenantLicences: "tenant-licences",
  Licence: "licence",
  LicenceTypes: "licence-types",
  SourceTypes: "source-types",
  SalesProducts: "sales-products",
  SalesProductsGroups: "sales-products-groups",
  ApplicationsRef: "applications-ref-data",
  TenantDefaultUserAuthentication: "tenant-default-user-auth",
  TenantNetworkAuthenticationSetting: "tenant-auth-network-setting",
  BillingContact: "billing-contact",
  BillingHistory: "billing-history",
  BillingInvoice: "billing-invoice",
  TenantPaymentMethods: "tenant-payment-methods",
  TenantDefaultPaymentMethod: "tenant-default-payment-methods",
  TenantStripeCustomer: "tenant-stripe-customer",
  ChildTenants: "child-tenants",
  CustomerSubscriptionsProducts: "customer-subscriptions-products",
  HierarchyTypesRef: "hierarchyTypes-ref-data",
  TenantPromotionCodes: "tenant-promotion-codes",
  ComponentsDefs: "components-defs",
  ComponentsLite: "components-lite",
  Components: "components",
  ComponentActions: "component-actions",
  ComponentActionsRequests: "components-actions-requests",
  ComponentSettings: "component-settings",
  ComponentSetting: "component-setting",
  ComponentWorkflowRunInstance: "component-workflow-run-instance",
  ComponentManagerWorkflowRunInstance:
    "component-manager-workflow-run-instance",
  RestrictionTypesRefData: "restriction-types-ref-data"
};

// Tenant
export const useTenantsPltQuery = (
  args?: Omit<TenantSearchArgs, "pageParam">,
  options?: Omit<
    UseInfiniteQueryOptions<PagingResponse<Tenant>, HttpError>,
    "queryKey" | "queryFn"
  >
) => {
  const { platformGateway } = useGateways();

  return useInfiniteQuery<PagingResponse<Tenant>, HttpError>(
    [PltCacheKeys.Tenants, args],
    page => {
      return platformGateway.getTenants({
        ...args,
        pageParam: page.pageParam
      });
    },
    {
      ...options,
      getNextPageParam: lastPage => {
        return lastPage.next;
      }
    }
  );
};

export const useTenantQuery = (
  id: string,
  options?: Omit<UseQueryOptions<Tenant, HttpError>, "queryKey" | "queryFn">
) => {
  const { platformGateway } = useGateways();
  return useQuery<Tenant, HttpError>(
    [PltCacheKeys.Tenants, id],
    () => platformGateway.getTenant(id),
    {
      ...options,
      // Never fetch /tenants/null|undefined: a falsy id 400s and poisons the
      // tenant cache. A caller can still explicitly disable, but can never
      // force a fetch with no id.
      enabled: (options?.enabled ?? true) && !!id
    }
  );
};

export const useTenantsByCrmIdQuery = (
  crmId: string,
  options?: Omit<UseQueryOptions<Tenant[], HttpError>, "queryKey" | "queryFn">
) => {
  const { platformGateway } = useGateways();
  return useQuery<Tenant[], HttpError>(
    [PltCacheKeys.Tenants, crmId],
    () => platformGateway.getTenantsByCrmId(crmId),
    {
      ...options
    }
  );
};

export const useUpdateTenantCustomerAccount = () => {
  const { platformGateway } = useGateways();
  const { error, success } = useFeedbackContext();
  const queryClient = useQueryClient();

  return useMutation<
    Tenant,
    HttpError,
    { tenantId: guid; customerTenantId: string }
  >(
    ({ tenantId, customerTenantId }) => {
      return platformGateway.updateTenantCustomerAccount(
        tenantId,
        customerTenantId
      );
    },
    {
      onSuccess: async tenantUpdate => {
        queryClient.setQueryData(
          [PltCacheKeys.Tenants, tenantUpdate.id],
          tenantUpdate
        );

        await queryClient.invalidateQueries([
          PltCacheKeys.ChildTenants,
          tenantUpdate.customerTenantId
        ]);
        success("Tenant details updated!");
      },
      onError: err => {
        error(err);
      }
    }
  );
};

export const useTenantFeatureQuery = (tenantId: guid, featureCode: string) => {
  const { platformGateway } = useGateways();

  return useQuery<TenantFeatureDetails | undefined>(
    [PltCacheKeys.TenantFeature, { tenantId, featureCode }],
    async () => {
      return await platformGateway.getTenantFeature(tenantId, featureCode);
    }
  );
};

export const useEnableDisableTenantFeatureMutation = () => {
  const { platformGateway } = useGateways();
  const queryClient = useQueryClient();

  return useMutation<
    any,
    Error,
    { tenantId: guid; featureCode: TenantFeature; action: FeatureAction }
  >(
    ({ tenantId, featureCode, action }) =>
      platformGateway.enableDisableFeature(tenantId, featureCode, action),
    {
      onSuccess: async (_, { tenantId, featureCode, action }) => {
        // Set the cache so that the link is disabled until the cache time expires and is
        // rechecked since provisioning a feature is an asynchronous process
        queryClient.setQueryData(
          [PltCacheKeys.TenantFeature, { tenantId, featureCode, action }],
          {
            id: "unknown",
            tenantId,
            feature: featureCode,
            action
          }
        );
      }
    }
  );
};

export const useEncryptTenantResourcesMutation = () => {
  const { platformGateway } = useGateways();
  const queryClient = useQueryClient();

  return useMutation<any, Error, { tenantId: string }>(
    ({ tenantId }) => platformGateway.encryptTenantResources(tenantId),
    {
      onSuccess: async (_, { tenantId }) => {
        // Set the cache so that the link is disabled until the cache time expires and is rechecked since this is an asynchronous process
        queryClient.setQueryData([PltCacheKeys.TenantFeature, { tenantId }], {
          id: "unknown",
          tenantId
        });
      }
    }
  );
};

export const useActivateTenantMutation = () => {
  const { platformGateway } = useGateways();
  const queryClient = useQueryClient();

  return useMutation<void, Error, { tenantId: string }>(
    ({ tenantId }) => platformGateway.activateTenant(tenantId),
    {
      onSuccess: async (_, { tenantId }) => {
        // Set the cache so that the link is disabled until the cache time expires and is rechecked since this is an asynchronous process
        queryClient.setQueryData([PltCacheKeys.TenantFeature, { tenantId }], {
          id: "unknown",
          tenantId
        });
      }
    }
  );
};

export const useDeactivateTenantMutation = () => {
  const { platformGateway } = useGateways();
  const queryClient = useQueryClient();

  return useMutation<void, Error, { tenantId: string }>(
    ({ tenantId }) => platformGateway.deactivateTenant(tenantId),
    {
      onSuccess: async (_, { tenantId }) => {
        // Set the cache so that the link is disabled until the cache time expires and is rechecked since this is an asynchronous process
        queryClient.setQueryData([PltCacheKeys.TenantFeature, { tenantId }], {
          id: "unknown",
          tenantId
        });
      }
    }
  );
};

export const useUpdateTenantIsTemporary = () => {
  const { platformGateway } = useGateways();
  const queryClient = useQueryClient();

  return useMutation<Tenant, Error, { tenantId: guid; isTemporary: boolean }>(
    ({ tenantId, isTemporary }) => {
      return platformGateway.updateTenantIsTemporary(tenantId, isTemporary);
    },
    {
      onSuccess: async tenantUpdate => {
        const tenants = queryClient.getQueryData<Tenant[]>([
          PltCacheKeys.Tenants,
          null
        ]);

        const tenant = tenants?.find(t => t.id === tenantUpdate.id);
        if (tenant) {
          cacheUpsertSingleItemInArray({
            queryClient,
            queryKey: [PltCacheKeys.Tenants, null],
            item: {
              ...tenant,
              isTemporary: tenantUpdate.isTemporary,
              eTag: tenantUpdate.eTag
            }
          });
        }
      }
    }
  );
};

export const useTenantDefaultUserAuth = (tenantId: guid) => {
  const { platformGateway } = useGateways();

  return useQuery<TenantDefaultUsersAuthenticationDto, HttpError>(
    [PltCacheKeys.TenantDefaultUserAuthentication, tenantId],
    async () => {
      try {
        return await platformGateway.getTenantDefaultUsersAuthentication(
          tenantId
        );
      } catch (e) {
        if (e instanceof NotFoundError) {
          return undefined;
        }
        return e;
      }
    }
  );
};

export const useAddTenantDefaultUserAuth = () => {
  const { platformGateway } = useGateways();
  const { success } = useFeedbackContext();

  const queryClient = useQueryClient();

  return useMutation<
    TenantDefaultUsersAuthenticationDto,
    HttpError,
    TenantDefaultUsersAuthenticationDto
  >(platformGateway.addTenantDefaultUsersAuthentication, {
    onSuccess: async dto => {
      queryClient.setQueryData(
        [PltCacheKeys.TenantDefaultUserAuthentication, dto.id],
        dto
      );
      success("Tenant details have been updated successfully!");
    }
  });
};

export const useUpdateTenantDefaultUserAuth = () => {
  const { platformGateway } = useGateways();
  const { success } = useFeedbackContext();
  const queryClient = useQueryClient();

  return useMutation<
    TenantDefaultUsersAuthenticationDto,
    HttpError,
    TenantDefaultUsersAuthenticationDto
  >(platformGateway.updateTenantDefaultUsersAuthentication, {
    onSuccess: async dto => {
      queryClient.setQueryData(
        [PltCacheKeys.TenantDefaultUserAuthentication, dto.id],
        dto
      );
      success("Tenant details have been updated successfully!");
    }
  });
};

export const useTenantNetworkAuthenticationSettings = (
  tenantId: string,
  query?: TenantNetworkAuthenticationSettingsQuery
) => {
  const { platformGateway } = useGateways();

  return useQuery<TenantNetworkAuthenticationSettingsDto[], HttpError>(
    [PltCacheKeys.TenantNetworkAuthenticationSetting, tenantId],
    async () => {
      try {
        return await platformGateway.getTenantNetworkAuthenticationSettings(
          tenantId,
          query
        );
      } catch (e) {
        if (e instanceof NotFoundError) {
          return undefined;
        }
        return e;
      }
    }
  );
};

export const useAddTenantNetworkAuthenticationSetting = () => {
  const { platformGateway } = useGateways();
  const { success } = useFeedbackContext();

  const queryClient = useQueryClient();

  return useMutation<
    TenantNetworkAuthenticationSettingsDto,
    HttpError,
    TenantNetworkAuthenticationSettingsPayload
  >(platformGateway.addTenantNetworkAuthenticationSettings, {
    onSuccess: async dto => {
      await queryClient.invalidateQueries([
        PltCacheKeys.TenantNetworkAuthenticationSetting,
        dto.tenantId
      ]);

      success("Tenant details have been updated successfully!");
    }
  });
};

export const useUpdateTenantNetworkAuthenticationSetting = () => {
  const { platformGateway } = useGateways();
  const { success } = useFeedbackContext();

  const queryClient = useQueryClient();

  return useMutation<
    TenantNetworkAuthenticationSettingsDto,
    HttpError,
    { id: string; payload: TenantNetworkAuthenticationSettingsPayload }
  >(
    ({ id, payload }) =>
      platformGateway.updateTenantNetworkAuthenticationSettings(id, payload),
    {
      onSuccess: async dto => {
        await queryClient.invalidateQueries([
          PltCacheKeys.TenantNetworkAuthenticationSetting,
          dto.tenantId
        ]);

        success("Tenant details have been updated successfully!");
      }
    }
  );
};

export const useDeleteTenantNetworkAuthenticationSetting = (
  tenantId: string
) => {
  const { platformGateway } = useGateways();
  const { success } = useFeedbackContext();

  const queryClient = useQueryClient();

  return useMutation<void, HttpError, string>(
    platformGateway.deleteTenantNetworkAuthenticationSettings,
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries([
          PltCacheKeys.TenantNetworkAuthenticationSetting,
          tenantId
        ]);

        success("Tenant details have been updated successfully!");
      }
    }
  );
};

export const useToggleTenantNetworkAuthenticationSetting = () => {
  const { platformGateway } = useGateways();
  const { success } = useFeedbackContext();

  const queryClient = useQueryClient();

  return useMutation<
    TenantNetworkAuthenticationSettingsDto,
    HttpError,
    { id: string; disable?: boolean }
  >(
    ({ id, disable }) =>
      !disable
        ? platformGateway.enableTenantNetworkAuthenticationSettings(id)
        : platformGateway.disableTenantNetworkAuthenticationSettings(id),
    {
      onSuccess: async dto => {
        await queryClient.invalidateQueries([
          PltCacheKeys.TenantNetworkAuthenticationSetting,
          dto.tenantId
        ]);

        success("Tenant details have been updated successfully!");
      }
    }
  );
};

export const useChildTenants = (
  customerTenantId: string,
  options?: Omit<UseQueryOptions<Tenant[], HttpError>, "queryKey" | "queryFn">
) => {
  const { platformGateway } = useGateways();

  return useQuery<Tenant[], HttpError>(
    [PltCacheKeys.ChildTenants, customerTenantId],
    async () => {
      try {
        return await platformGateway.getChildTenants(customerTenantId);
      } catch (e) {
        if (e instanceof NotFoundError) {
          return undefined;
        }
        return e;
      }
    },
    options
  );
};

export const useUpdateTenant = () => {
  const { platformGateway } = useGateways();
  const { success } = useFeedbackContext();
  const queryClient = useQueryClient();

  return useMutation<Tenant, HttpError, TenantDto>(
    platformGateway.updateTenant,
    {
      onSuccess: async dto => {
        const prevData = queryClient.getQueryData<Tenant>([
          PltCacheKeys.Tenants,
          dto.id
        ]);
        // update current get tenant query
        queryClient.setQueryData([PltCacheKeys.Tenants, dto.id], {
          ...prevData,
          ...dto
        });

        // get all search tenant queries where query key contains CacheKeys.Tenants and data is in array
        const queries = queryClient.getQueryCache().getAll();
        const tenantQueries = queries.filter(
          s =>
            s.queryKey.includes(PltCacheKeys.Tenants) &&
            Array.isArray(s.state.data)
        );

        // update all search tenants queries
        tenantQueries.forEach(query => {
          const prevData = queryClient.getQueryData<Tenant>(query.queryKey);
          cacheUpsertSingleItemInArray({
            queryClient,
            queryKey: query.queryKey,
            item: {
              ...prevData,
              ...dto
            }
          });
        });

        success("Tenant details have been updated successfully!");
      }
    }
  );
};

export const useCreateTenant = () => {
  const { platformGateway } = useGateways();
  const { success } = useFeedbackContext();
  const queryClient = useQueryClient();

  return useMutation<Tenant, HttpError, CreateTenantDto>(
    platformGateway.createTenant,
    {
      onSuccess: async dto => {
        cacheUpsertSingleItemInArray({
          queryClient,
          queryKey: [PltCacheKeys.Tenants],
          item: dto
        });

        await queryClient.invalidateQueries([
          PltCacheKeys.ChildTenants,
          dto.customerTenantId
        ]);

        success("Tenant has been created successfully!");
      }
    }
  );
};

// Plt Users
export const useUser = (tenantId: guid, userId: string) => {
  const { platformGateway } = useGateways();
  return useQuery<PltUser, HttpError>([PltCacheKeys.User, userId], () =>
    platformGateway.getUser(tenantId, userId)
  );
};

export const useSearchUsers = (
  args?: TenantUserSearchArgs,
  options?: Omit<
    UseQueryOptions<PltUser[]>,
    "queryKey" | "queryFn" | "onSuccess"
  >
) => {
  const { platformGateway } = useGateways();

  return useQuery<PltUser[], HttpError>(
    [
      PltCacheKeys.UsersSearch,
      args?.tenantId,
      args?.searchTerm,
      args?.bpIdUserId,
      args?.hasBpId,
      args?.hasExternalId,
      args?.isInactive,
      args?.userStatusCode,
      args?.sortField,
      args?.sortAscending
    ],
    async () => await platformGateway.searchUsers(args),
    {
      ...options
    }
  );
};

export const useCreateUser = () => {
  const { platformGateway } = useGateways();
  const { success } = useFeedbackContext();
  const queryClient = useQueryClient();

  return useMutation<PltUser, HttpError, NewPltUser>(
    platformGateway.createUser,
    {
      onSuccess: async dto => {
        const queries = queryClient.getQueryCache().getAll();
        const userSearchQuery = queries.filter(q =>
          q.queryKey.includes(PltCacheKeys.UsersSearch)
        );

        userSearchQuery.forEach(query => {
          cacheUpsertSingleItemInArray({
            queryClient,
            queryKey: query.queryKey,
            item: dto,
            asFirstItem: true
          });
        });

        success("A new user has been created successfully!");
      }
    }
  );
};

export const useUpdateUser = () => {
  const { platformGateway } = useGateways();
  const { success } = useFeedbackContext();
  const queryClient = useQueryClient();

  return useMutation<PltUser, HttpError, PltUser>(platformGateway.updateUser, {
    onSuccess: async dto => {
      // Force invalidate of the query for the updated user
      // Because the user data is being used on a form, we want to use "Refetch" in place of invalidate
      // as the form values are loaded before the "invalidate" action gets the new values from the api
      queryClient.setQueryData([PltCacheKeys.User, dto.id], dto);
      await queryClient.refetchQueries([PltCacheKeys.User, dto.id]);
      await queryClient.refetchQueries({
        queryKey: [PltCacheKeys.UsersSearch]
      });

      success("The user data has been updated successfully!");
    }
  });
};

export const useUserDefaultAuth = (tenantId: guid, userId: guid) => {
  const { platformGateway } = useGateways();

  return useQuery<UserDefaultAuthenticationDto, HttpError>(
    [PltCacheKeys.UserDefaultAuthentication, tenantId, userId],
    async () => {
      try {
        return await platformGateway.getUserDefaultAuthentication(
          tenantId,
          userId
        );
      } catch (e) {
        if (e instanceof NotFoundError) {
          return undefined;
        }
        return e;
      }
    }
  );
};

export const useAddUserDefaultAuth = () => {
  const { platformGateway } = useGateways();
  const { success } = useFeedbackContext();
  const queryClient = useQueryClient();

  return useMutation<
    UserDefaultAuthenticationDto,
    HttpError,
    UserDefaultAuthenticationDto
  >(platformGateway.addUserDefaultAuthentication, {
    onSuccess: async dto => {
      queryClient.setQueryData(
        [PltCacheKeys.UserDefaultAuthentication, dto.tenantId, dto.userId],
        dto
      );
      success("User auth details have been updated successfully!");
    }
  });
};

export const useUpdateUserDefaultAuth = () => {
  const { platformGateway } = useGateways();
  const { success } = useFeedbackContext();
  const queryClient = useQueryClient();

  return useMutation<
    UserDefaultAuthenticationDto,
    HttpError,
    UserDefaultAuthenticationDto
  >(platformGateway.updateUserDefaultAuthentication, {
    onSuccess: async dto => {
      queryClient.setQueryData(
        [PltCacheKeys.UserDefaultAuthentication, dto.tenantId, dto.userId],
        dto
      );
      success("User auth details have been updated successfully!");
    }
  });
};

export const useUserNetworkAuthenticationSettings = (
  tenantId: string,
  userId: string,
  query?: TenantNetworkAuthenticationSettingsQuery
) => {
  const { platformGateway } = useGateways();

  return useQuery<UserNetworkAuthenticationSettingsDto[], HttpError>(
    [PltCacheKeys.UserNetworkAuthenticationSetting, tenantId, userId],
    async () => {
      try {
        return await platformGateway.getUserNetworkAuthenticationSettings(
          tenantId,
          userId,
          query
        );
      } catch (e) {
        if (e instanceof NotFoundError) {
          return undefined;
        }
        return e;
      }
    }
  );
};

export const useAddUserNetworkAuthenticationSetting = () => {
  const { platformGateway } = useGateways();
  const { success } = useFeedbackContext();

  const queryClient = useQueryClient();

  return useMutation<
    UserNetworkAuthenticationSettingsDto,
    HttpError,
    UserNetworkAuthenticationSettingsPayload
  >(platformGateway.addUserNetworkAuthenticationSettings, {
    onSuccess: async dto => {
      await queryClient.invalidateQueries([
        PltCacheKeys.UserNetworkAuthenticationSetting,
        dto.tenantId,
        dto.userId
      ]);

      success("User details have been updated successfully!");
    }
  });
};

export const useUpdateUserNetworkAuthenticationSetting = () => {
  const { platformGateway } = useGateways();
  const { success } = useFeedbackContext();

  const queryClient = useQueryClient();

  return useMutation<
    UserNetworkAuthenticationSettingsDto,
    HttpError,
    { id: string; payload: UserNetworkAuthenticationSettingsPayload }
  >(
    ({ id, payload }) =>
      platformGateway.updateUserNetworkAuthenticationSettings(id, payload),
    {
      onSuccess: async dto => {
        await queryClient.invalidateQueries([
          PltCacheKeys.UserNetworkAuthenticationSetting,
          dto.tenantId,
          dto.userId
        ]);

        success("User details have been updated successfully!");
      }
    }
  );
};

export const useDeleteUserNetworkAuthenticationSetting = (
  tenantId: string,
  userId: string
) => {
  const { platformGateway } = useGateways();
  const { success } = useFeedbackContext();

  const queryClient = useQueryClient();

  return useMutation<void, HttpError, string>(
    platformGateway.deleteUserNetworkAuthenticationSettings,
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries([
          PltCacheKeys.UserNetworkAuthenticationSetting,
          tenantId,
          userId
        ]);

        success("User details have been updated successfully!");
      }
    }
  );
};

export const useToggleUserNetworkAuthenticationSetting = () => {
  const { platformGateway } = useGateways();
  const { success } = useFeedbackContext();

  const queryClient = useQueryClient();

  return useMutation<
    UserNetworkAuthenticationSettingsDto,
    HttpError,
    { id: string; disable?: boolean }
  >(
    ({ id, disable }) =>
      !disable
        ? platformGateway.enableUserNetworkAuthenticationSettings(id)
        : platformGateway.disableUserNetworkAuthenticationSettings(id),
    {
      onSuccess: async dto => {
        await queryClient.invalidateQueries([
          PltCacheKeys.UserNetworkAuthenticationSetting,
          dto.tenantId,
          dto.userId
        ]);

        success("User details have been updated successfully!");
      }
    }
  );
};

export const useFeature = (params?: GetFeatureParams) => {
  const { platformGateway } = useGateways();
  return useQuery<Feature[]>([PltCacheKeys.Feature, params], async () => {
    return await platformGateway.getFeature(params);
  });
};

export const useSecurityRolesAndPermissions = () => {
  const { platformGateway } = useGateways();

  return useQuery<SecurityRolesAndPermissionsDto, HttpError>(
    [PltCacheKeys.SecurityRolesAndPermissions],
    () => platformGateway.getSecurityRolesAndPermissions()
  );
};

export const useTenantFeatures = (params: GetTenantFeatureParams) => {
  const { platformGateway } = useGateways();
  return useQuery<TenantFeatureDetails[]>(
    [PltCacheKeys.TenantFeatures],
    async () => {
      return await platformGateway.getTenantFeatures(params);
    }
  );
};

export const useBusinessRoles = (businessRoles?: string[]) => {
  const { platformGateway } = useGateways();
  return useQuery<BusinessRoleDto[], HttpError>(
    [PltCacheKeys.BusinessRoles],
    async () => {
      return await platformGateway.getBusinessRole(businessRoles);
    },
    {
      cacheTime: Infinity,
      staleTime: Infinity
    }
  );
};

export const useSecurityRoleSets = () => {
  const { platformGateway } = useGateways();
  return useQuery<SecurityRoleSetDto[], HttpError>(
    [PltCacheKeys.SecurityRolesSets],
    async () => {
      return await platformGateway.getSecurityRoleSets();
    },
    {
      cacheTime: Infinity,
      staleTime: Infinity
    }
  );
};

export const useSecurityRoles = () => {
  const { platformGateway } = useGateways();
  return useQuery<SecurityRoleDto[], HttpError>(
    [PltCacheKeys.SecurityRoles],
    async () => {
      return await platformGateway.getSecurityRoles();
    },
    {
      cacheTime: Infinity,
      staleTime: Infinity
    }
  );
};

export const useSecurityBusinessRoles = (
  query: GetBusinessSecurityRolesParams
) => {
  const { platformGateway } = useGateways();
  return useQuery<BusinessSecurityRoleDto[], HttpError>(
    [PltCacheKeys.SecurityBusinessRoles, query],
    () => platformGateway.getBusinessSecurityRoles(query)
  );
};

export const useUserRoles = (
  tenantId: string,
  userId: string,
  options?: Omit<UseQueryOptions<UserSecRolesDto>, "queryKey" | "queryFn">
) => {
  const { platformGateway } = useGateways();
  return useQuery<UserSecRolesDto, HttpError>(
    [PltCacheKeys.UserRoles, tenantId, userId],
    () => platformGateway.getUserRoles(tenantId, userId),
    options
  );
};

export const useUserPermissions = (
  tenantId: string,
  userId: string,
  options?: Omit<
    UseQueryOptions<UserSecPermissionDto[]>,
    "queryKey" | "queryFn"
  >
) => {
  const { platformGateway } = useGateways();
  return useQuery<UserSecPermissionDto[], HttpError>(
    [PltCacheKeys.UserPermissions, tenantId, userId],
    () => platformGateway.getUserPermissions(tenantId, userId),
    options
  );
};

export const useAssignedUserSecurityRoles = (
  args: { tenantId: string; userId: string; businessRoles: string[] },
  options?: Omit<
    UseQueryOptions<SecurityRoleAssignmentsDto[], HttpError>,
    "queryKey" | "queryFn" | "enabled"
  >
) => {
  const { tenantId, userId, businessRoles } = args;
  const { platformGateway } = useGateways();
  return useQuery<SecurityRoleAssignmentsDto[], HttpError>(
    [PltCacheKeys.UserAssignedSecurityRoles, tenantId, userId, businessRoles],
    () =>
      platformGateway.getUserSecurityRolesAssignments(
        tenantId,
        userId,
        businessRoles
      ),
    { enabled: !!businessRoles.length, ...options }
  );
};

export const useUpdateUserRoles = (tenantId: string, userId: string) => {
  const { platformGateway } = useGateways();
  const queryClient = useQueryClient();
  return useMutation<UserSecRolesDto, HttpError, UpdateUserRolesPayload>(
    payload =>
      platformGateway.updateUserSecurityRoles(tenantId, userId, payload),
    {
      onSuccess: async dto => {
        await queryClient.invalidateQueries([
          PltCacheKeys.UserRoles,
          tenantId,
          dto.id
        ]);

        const queries = queryClient.getQueryCache().getAll();
        const userAssignedSecurityRolesQueries = queries.filter(
          q =>
            q.queryKey.includes(PltCacheKeys.UserAssignedSecurityRoles) &&
            q.queryKey.includes(tenantId) &&
            q.queryKey.includes(dto.id)
        );

        await Promise.all(
          userAssignedSecurityRolesQueries.map(query =>
            queryClient.invalidateQueries(query.queryKey)
          )
        );
      }
    }
  );
};

// Licences types
export const useLicenceTypes = () => {
  const { platformGateway } = useGateways();
  return useQuery<LicenceTypeDto[], HttpError>(
    [PltCacheKeys.LicenceTypes],
    platformGateway.getLicenceTypes
  );
};

// source types
export const useSourceTypes = () => {
  const { platformGateway } = useGateways();
  return useQuery<SourceTypeDto[], HttpError>(
    [PltCacheKeys.SourceTypes],
    platformGateway.getSourceTypes,
    {
      cacheTime: Infinity,
      staleTime: Infinity
    }
  );
};

export const useLicenceType = (
  code: string,
  options?: Omit<UseQueryOptions<LicenceTypeDto>, "queryKey" | "queryFn">
) => {
  const { platformGateway } = useGateways();
  const queryClient = useQueryClient();

  return useQuery<LicenceTypeDto, HttpError>(
    [PltCacheKeys.LicenceTypes, code],
    () => platformGateway.getLicenceType(code),
    {
      ...options,
      initialData: () => {
        const cache = queryClient.getQueryData<LicenceTypeDto[]>([
          PltCacheKeys.LicenceTypes
        ]);
        return cache?.find(d => d.code === code);
      }
    }
  );
};

export const useAddLicenceType = () => {
  const { platformGateway } = useGateways();
  const { success } = useFeedbackContext();
  const queryClient = useQueryClient();

  return useMutation<LicenceTypeDto, HttpError, LicenceTypeDto>(
    platformGateway.addLicenceType,
    {
      onSuccess: async dto => {
        cacheUpsertSingleItemInArray({
          queryClient,
          queryKey: [PltCacheKeys.LicenceTypes],
          item: dto,
          isIdentityEqual: item => item.code === dto.code
        });
        success("A new licence type has been added successfully!");
        queryClient.invalidateQueries([PltCacheKeys.LicenceTypes, dto.code]);
      }
    }
  );
};

export const useUpdateLicenceType = () => {
  const { platformGateway } = useGateways();
  const { success } = useFeedbackContext();
  const queryClient = useQueryClient();

  return useMutation<LicenceTypeDto, HttpError, LicenceTypeDto>(
    platformGateway.updateLicenceType,
    {
      onSuccess: async dto => {
        cacheUpsertSingleItemInArray({
          queryClient,
          queryKey: [PltCacheKeys.LicenceTypes],
          item: dto,
          isIdentityEqual: item => item.code === dto.code
        });
        success("The licence type has been updated successfully!");
        queryClient.invalidateQueries([PltCacheKeys.LicenceTypes, dto.code]);
      }
    }
  );
};

export const useToggleLicenceType = () => {
  const { platformGateway } = useGateways();
  const { error, success } = useFeedbackContext();
  const queryClient = useQueryClient();

  return useMutation<void, Error, { code: string; disable?: boolean }>(
    ({ code, disable }) =>
      disable
        ? platformGateway.disableLicenceType(code)
        : platformGateway.enableLicenceType(code),
    {
      onSuccess: async (dto, args) => {
        await queryClient.invalidateQueries([PltCacheKeys.LicenceTypes]);
        success(
          `A new licence type has been ${
            args.disable ? "disabled" : "enabled"
          }!`
        );
      },
      onError: e => {
        error(e.message);
      }
    }
  );
};

// Licences
const updateLicencesQueryKey = (
  queryClient: QueryClient,
  dto: LicenceDto,
  asFirstItem?: boolean
) => {
  const queries = queryClient.getQueryCache().getAll();
  const userSearchQuery = queries.filter(q =>
    q.queryKey.includes(PltCacheKeys.TenantLicences)
  );

  userSearchQuery.forEach(query => {
    cacheUpsertSingleItemInArray({
      queryClient,
      queryKey: query.queryKey,
      item: dto,
      asFirstItem
    });
  });
};

export const useLicence = (
  id: string,
  options?: Omit<UseQueryOptions<LicenceDto>, "queryKey" | "queryFn">
) => {
  const { platformGateway } = useGateways();

  return useQuery<LicenceDto>(
    [PltCacheKeys.Licence, id],
    () => platformGateway.getLicence(id),
    {
      ...options
    }
  );
};

export const useCanUnlinkTenant = (args: UnlinkTenantDto) => {
  const { platformGateway } = useGateways();

  return useQuery<CanUnlinkDto, Error>(
    ["canUnlinkTenant", args.tenantId, args.childTenantId],
    () => platformGateway.canUnlinkTenant(args)
  );
};

export const useUnlinkTenant = (args: UnlinkTenantDto) => {
  const { platformGateway } = useGateways();
  const { success, error } = useFeedbackContext();
  return useMutation<void, Error, UnlinkTenantDto>(
    () => platformGateway.unlinkTenant(args),
    {
      onSuccess: () => {
        success("tenant unlinked");
      },
      onError: () => {
        error("Error unlinking tenant");
      }
    }
  );
};

export const useTransferTenant = (args: TransferTenantDto) => {
  const { platformGateway } = useGateways();
  const { success, error } = useFeedbackContext();
  return useMutation<void, Error, TransferTenantDto>(
    () => platformGateway.transferTenant(args),
    {
      onSuccess: () => {
        success("licences transferred");
      },
      onError: () => {
        error("Error transferring licences");
      }
    }
  );
};

export const useLicences = (args: GetLicencesArgs) => {
  const { platformGateway } = useGateways();

  return useQuery<LicenceDto[], HttpError>(
    [PltCacheKeys.TenantLicences, args],
    () => platformGateway.getLicences(args)
  );
};

export const useAddLicence = () => {
  const { platformGateway } = useGateways();
  const { success } = useFeedbackContext();
  const queryClient = useQueryClient();

  return useMutation<LicenceDto, HttpError, AddLicenceDto>(
    platformGateway.addLicence,
    {
      onSuccess: dto => {
        updateLicencesQueryKey(queryClient, dto, true);
        success("A new licence has been added successfully!");
      }
    }
  );
};

export const useUpdateLicence = () => {
  const { platformGateway } = useGateways();
  const { success } = useFeedbackContext();
  const queryClient = useQueryClient();

  return useMutation<LicenceDto, HttpError, LicenceDto>(
    platformGateway.updateLicence,
    {
      onSuccess: async dto => {
        updateLicencesQueryKey(queryClient, dto);
        queryClient.setQueryData([PltCacheKeys.Licence, dto.id], dto);
        success("The licence has been updated successfully!");
      }
    }
  );
};

export const useRemoveLicence = () => {
  const { platformGateway } = useGateways();
  const { success } = useFeedbackContext();
  const queryClient = useQueryClient();

  return useMutation<LicenceDto, Error, string>(platformGateway.removeLicence, {
    onSuccess: async dto => {
      cacheDeleteSingleItemInArray({
        queryClient,
        queryKey: [PltCacheKeys.TenantLicences, dto.tenantId],
        id: dto.id
      });
      success("The licence has been inactivated!");
    }
  });
};

// Products
const getSalesProductsCurrentQueryKey = (queryClient: QueryClient) => {
  const queries = queryClient.getQueryCache();
  const query = queries
    .getAll()
    .find(s => s.queryKey.includes(PltCacheKeys.SalesProducts));

  if (query) {
    return query.queryKey;
  }

  return [PltCacheKeys.SalesProducts];
};

export const useSalesProducts = (
  args?: GetSalesProductsArgs,
  options?: Omit<UseQueryOptions<SalesProductDto[]>, "queryKey" | "queryFn">
) => {
  const queryKey = args
    ? [PltCacheKeys.SalesProducts, args]
    : PltCacheKeys.SalesProducts;

  const { platformGateway } = useGateways();
  return useQuery<SalesProductDto[], HttpError>(
    queryKey,
    async () => {
      return await platformGateway.getSalesProducts(args);
    },
    options
  );
};

export const useSalesProduct = (
  id: string,
  options?: Omit<
    UseQueryOptions<SalesProductDto, HttpError>,
    "queryKey" | "queryFn"
  >
) => {
  const { platformGateway } = useGateways();
  const queryClient = useQueryClient();

  return useQuery<SalesProductDto, HttpError>(
    [PltCacheKeys.SalesProducts, id],
    () => platformGateway.getSalesProduct(id),
    {
      ...options,
      initialData: () => {
        const cache = queryClient.getQueryData<SalesProductDto[]>(
          PltCacheKeys.SalesProducts
        );
        return cache?.find(d => d.id === id);
      }
    }
  );
};

export const useAddSalesProduct = () => {
  const { platformGateway } = useGateways();
  const { success } = useFeedbackContext();
  const queryClient = useQueryClient();

  return useMutation<SalesProductDto, HttpError, AddSalesProductDto>(
    platformGateway.addSalesProduct,
    {
      onSuccess: dto => {
        const queryKey = getSalesProductsCurrentQueryKey(queryClient);
        cacheUpsertSingleItemInArray({
          queryClient,
          queryKey,
          item: dto
        });
        success("A new product has been added successfully!");
      }
    }
  );
};

export const useUpdateSalesProduct = () => {
  const { platformGateway } = useGateways();
  const { success } = useFeedbackContext();
  const queryClient = useQueryClient();

  return useMutation<SalesProductDto, HttpError, SalesProductDto>(
    platformGateway.updateSalesProduct,
    {
      onSuccess: dto => {
        const queryKey = getSalesProductsCurrentQueryKey(queryClient);
        cacheUpsertSingleItemInArray({
          queryClient,
          queryKey,
          item: dto
        });
        queryClient.setQueryData([PltCacheKeys.SalesProducts, dto.id], dto);
        success("The product has been updated successfully!");
      }
    }
  );
};

export const useResyncStripeProducts = () => {
  const { platformGateway } = useGateways();
  const { error, success } = useFeedbackContext();

  return useMutation<void, HttpError>(
    () => platformGateway.resyncStripeProducts(),
    {
      onSuccess: () => {
        success("The Stripe products have been synced successfully!");
      },
      onError: () => {
        error("Error occurred while syncing the Stripe products!");
      }
    }
  );
};

export const useApplicationsRefData = () => {
  const { platformGateway } = useGateways();

  return useQuery<RefDataDto[], HttpError>(
    [PltCacheKeys.ApplicationsRef],
    platformGateway.getApplicationsRefData,
    {
      cacheTime: Infinity,
      staleTime: Infinity
    }
  );
};

export const useHierarchyTypesRefData = () => {
  const { platformGateway } = useGateways();

  return useQuery<RefDataDto[], HttpError>(
    [PltCacheKeys.HierarchyTypesRef],
    platformGateway.getHierarchyTypesRefData,
    {
      cacheTime: Infinity,
      staleTime: Infinity
    }
  );
};

// Billing and payment methods
export const useBillingContact = (
  tenantId: string,
  options?: Omit<UseQueryOptions<BillingContactDto>, "queryKey" | "queryFn">
) => {
  const { platformGateway } = useGateways();
  return useQuery<BillingContactDto>(
    [PltCacheKeys.BillingContact, tenantId],
    () => platformGateway.getBillingContact(tenantId),
    options
  );
};

export const useBillingHistory = (
  tenantId: string,
  options?: Omit<
    UseQueryOptions<InvoiceDto[], HttpError>,
    "queryKey" | "queryFn"
  >
) => {
  const { platformGateway } = useGateways();

  return useQuery<InvoiceDto[], HttpError>(
    [PltCacheKeys.BillingHistory, tenantId],
    () => platformGateway.getBillingHistory(tenantId),
    options
  );
};

export const useBillingInvoice = (
  tenantId: string,
  invoiceId: string,
  options?: Omit<UseQueryOptions<InvoiceDto, HttpError>, "queryKey" | "queryFn">
) => {
  const { platformGateway } = useGateways();

  return useQuery<InvoiceDto, HttpError>(
    [PltCacheKeys.BillingHistory, tenantId, invoiceId],
    () => platformGateway.getInvoice(tenantId, invoiceId),
    options
  );
};

export const usePayInvoice = () => {
  const queryClient = useQueryClient();
  const { platformGateway } = useGateways();
  const { error, success } = useFeedbackContext();

  return useMutation<
    InvoiceDto,
    HttpError,
    { tenantId: string; invoiceNumber: string }
  >(
    ({ tenantId, invoiceNumber }) =>
      platformGateway.payInvoice(tenantId, invoiceNumber),
    {
      onSuccess: (dto, args) => {
        cacheUpsertSingleItemInArray({
          queryClient,
          queryKey: [PltCacheKeys.BillingHistory, args.tenantId],
          item: dto
        });
        success("The invoice has been paid!");
      },
      onError: e => {
        error(e.message);
      }
    }
  );
};

export const useTenantPaymentMethods = (
  customerTenantId: string,
  options?: Omit<
    UseQueryOptions<PaymentMethodDto[], HttpError>,
    "queryKey" | "queryFn"
  >
) => {
  const { platformGateway } = useGateways();

  return useQuery<PaymentMethodDto[], HttpError>(
    [PltCacheKeys.TenantPaymentMethods, customerTenantId],
    () => platformGateway.getPaymentMethods(customerTenantId),
    options
  );
};

export const useTenantDefaultPaymentMethod = (
  customerTenantId: string,
  options?: Omit<
    UseQueryOptions<PaymentMethodDto, HttpError>,
    "queryKey" | "queryFn"
  >
) => {
  const { platformGateway } = useGateways();
  const queryClient = useQueryClient();

  return useQuery<PaymentMethodDto, HttpError>(
    [PltCacheKeys.TenantDefaultPaymentMethod, customerTenantId],
    () => platformGateway.getDefaultPaymentMethod(customerTenantId),
    {
      ...options,
      onSuccess: async dto => {
        cacheUpsertSingleItemInArray({
          queryClient,
          queryKey: [PltCacheKeys.TenantPaymentMethods, customerTenantId],
          item: dto
        });
        await invalidateCustomerSubscriptionsProductsQuery(
          queryClient,
          customerTenantId
        );
      }
    }
  );
};

export const useUpdateTenantPaymentMethods = () => {
  const queryClient = useQueryClient();
  const { platformGateway } = useGateways();
  const { error, success } = useFeedbackContext();

  return useMutation<PaymentMethodDto, HttpError, PaymentMethodUpdateArgs>(
    platformGateway.updatePaymentMethod,
    {
      onSuccess: async (dto, args) => {
        cacheUpsertSingleItemInArray({
          queryClient,
          queryKey: [PltCacheKeys.TenantPaymentMethods, args.customerTenantId],
          item: dto
        });
        await invalidateCustomerSubscriptionsProductsQuery(
          queryClient,
          args.customerTenantId
        );
        success("The method has been updated!");
      },
      onError: e => {
        error(e.message);
      }
    }
  );
};

export const useDeleteTenantPaymentMethods = (customerTenantId: string) => {
  const queryClient = useQueryClient();
  const { platformGateway } = useGateways();
  const { error, success } = useFeedbackContext();

  return useMutation<void, HttpError, string>(
    async paymentMethodId => {
      await platformGateway.deletePaymentMethod(
        customerTenantId,
        paymentMethodId
      );

      const methods =
        queryClient.getQueryData<PaymentMethodDto[]>([
          PltCacheKeys.TenantPaymentMethods,
          customerTenantId
        ]) ?? [];

      const cardToBeDeleted = methods.find(m => m.id === paymentMethodId);
      const remainedCards = methods.filter(m => m.id !== paymentMethodId);

      // In a case we delete the default payment method, we are setting the first remaining as default one!
      if (
        cardToBeDeleted &&
        cardToBeDeleted.isDefault &&
        remainedCards.length
      ) {
        await platformGateway.setupPaymentMethodAsDefault(
          customerTenantId,
          remainedCards[0].id
        );
      }
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries([
          PltCacheKeys.TenantPaymentMethods,
          customerTenantId
        ]);

        queryClient.removeQueries([
          PltCacheKeys.TenantDefaultPaymentMethod,
          customerTenantId
        ]);

        await invalidateCustomerSubscriptionsProductsQuery(
          queryClient,
          customerTenantId
        );
        success("The method has been deleted!");
      },
      onError: e => {
        error(e.message);
      }
    }
  );
};

export const useTenantPaymentMethodsAsDefault = (customerTenantId: string) => {
  const queryClient = useQueryClient();
  const { platformGateway } = useGateways();
  const { error, success } = useFeedbackContext();

  return useMutation<PaymentMethodDto, HttpError, string>(
    paymentMethodId =>
      platformGateway.setupPaymentMethodAsDefault(
        customerTenantId,
        paymentMethodId
      ),
    {
      onSuccess: async dto => {
        await queryClient.invalidateQueries([
          PltCacheKeys.TenantPaymentMethods,
          customerTenantId
        ]);

        await invalidateCustomerSubscriptionsProductsQuery(
          queryClient,
          customerTenantId
        );

        queryClient.setQueryData(
          [PltCacheKeys.TenantDefaultPaymentMethod, customerTenantId],
          dto
        );

        success("The method has been updated!");
      },
      onError: e => {
        error(e.message);
      }
    }
  );
};

export const useSetupTenantPaymentMethods = () => {
  const { platformGateway } = useGateways();
  const { error } = useFeedbackContext();

  return useMutation<PaymentMethodSetupDto, HttpError, string>(
    platformGateway.setupPaymentMethod,
    {
      onError: e => {
        error(e.message);
      }
    }
  );
};

// Stripe customer
export const useStripeCustomer = (
  tenantId: string,
  options?: Omit<
    UseQueryOptions<StripeCustomerDto, HttpError>,
    "queryKey" | "queryFn"
  >
) => {
  const { platformGateway } = useGateways();

  return useQuery<StripeCustomerDto, HttpError>(
    [PltCacheKeys.TenantStripeCustomer, tenantId],
    () => platformGateway.getStripeCustomer(tenantId),
    {
      ...options,
      onError: e => {
        if (e instanceof NotFoundError) {
          return undefined;
        }
        return e;
      }
    }
  );
};

export const useAddStripeCustomer = () => {
  const { platformGateway } = useGateways();
  const { error, success } = useFeedbackContext();
  const queryClient = useQueryClient();

  return useMutation<StripeCustomerDto, HttpError, AddStripeCustomerDto>(
    platformGateway.addStripeCustomer,
    {
      onSuccess: data => {
        queryClient.setQueryData(
          [PltCacheKeys.TenantStripeCustomer, data.tenantId],
          data
        );
        queryClient.invalidateQueries([PltCacheKeys.Tenants, data.tenantId]);

        success("A new Stripe customer has been created!");
      },
      onError: e => {
        error(e.message);
      }
    }
  );
};

// Subscriptions

const invalidateCustomerSubscriptionsProductsQuery = async (
  queryClient: QueryClient,
  customerTenantId: string
) => {
  await queryClient.invalidateQueries([
    PltCacheKeys.CustomerSubscriptionsProducts,
    customerTenantId
  ]);
};
export const useCustomerProducts = (
  customerTenantId: string,
  options?: Omit<
    UseQueryOptions<CustomerProductsDto, HttpError>,
    "queryKey" | "queryFn"
  >
) => {
  const { platformGateway } = useGateways();

  return useQuery<CustomerProductsDto, HttpError>(
    [PltCacheKeys.CustomerSubscriptionsProducts, customerTenantId],
    () => platformGateway.getCustomerProducts(customerTenantId),
    {
      ...options
    }
  );
};

export const useCustomerSubscriptionPreview = () => {
  const { platformGateway } = useGateways();

  return useMutation<
    SubscriptionPreviewDto,
    HttpError,
    PreviewSubscriptionPayload
  >(platformGateway.getCustomerSubscriptionPreview);
};

export const useAddCustomerSubscription = (customerTenantId: string) => {
  const { platformGateway } = useGateways();
  const { success } = useFeedbackContext();
  const queryClient = useQueryClient();

  return useMutation<SubscriptionPreviewDto, HttpError, AddSubscriptionPayload>(
    platformGateway.addCustomerSubscription,
    {
      onSuccess: async () => {
        await invalidateCustomerSubscriptionsProductsQuery(
          queryClient,
          customerTenantId
        );

        await queryClient.refetchQueries([
          PltCacheKeys.BillingHistory,
          customerTenantId
        ]);
        success("A new subscription has been updated!");
      }
    }
  );
};

export const useCancelCustomerSubscriptions = () => {
  const { platformGateway } = useGateways();
  const { error, success } = useFeedbackContext();
  const queryClient = useQueryClient();

  return useMutation<
    SubscriptionDto,
    HttpError,
    CancelCancelCustomerSubscriptionsArgs
  >(platformGateway.cancelCustomerSubscription, {
    onSuccess: async dto => {
      await invalidateCustomerSubscriptionsProductsQuery(
        queryClient,
        dto.customerTenantId
      );
      success("The subscription has been canceled!");
    },
    onError: e => {
      error(e.message);
    }
  });
};

export const useUndoCancellationCustomerSubscription = () => {
  const { platformGateway } = useGateways();
  const { error, success } = useFeedbackContext();
  const queryClient = useQueryClient();

  return useMutation<
    SubscriptionDto,
    HttpError,
    Omit<CancelCancelCustomerSubscriptionsArgs, "cancelImmediately">
  >(platformGateway.undoCancellationCustomerSubscription, {
    onSuccess: async dto => {
      await invalidateCustomerSubscriptionsProductsQuery(
        queryClient,
        dto.customerTenantId
      );
      success("The subscription cancellation has been updated!");
    },
    onError: e => {
      error(e.message);
    }
  });
};

export const useResetCustomerSubscriptionBillingDate = () => {
  const { platformGateway } = useGateways();
  const { error, success } = useFeedbackContext();
  const queryClient = useQueryClient();

  return useMutation<
    SubscriptionDto,
    HttpError,
    ResetCustomerSubscriptionBillingDateArgs
  >(platformGateway.resetCustomerSubscriptionBillingDate, {
    onSuccess: async dto => {
      await invalidateCustomerSubscriptionsProductsQuery(
        queryClient,
        dto.customerTenantId
      );
      success("The subscription billing date has been reset!");
    },
    onError: e => {
      error(e.message);
    }
  });
};

export const useTenantPromotionCodes = (
  tenantId: string,
  options?: Omit<
    UseQueryOptions<PromotionCodeDto[], HttpError>,
    "queryKey" | "queryFn"
  >
) => {
  const { platformGateway } = useGateways();

  return useQuery<PromotionCodeDto[], HttpError>(
    [PltCacheKeys.TenantPromotionCodes, tenantId],
    () => platformGateway.getTenantPromotionCodes(tenantId),
    options
  );
};

export const useTenantPromotionCode = (
  tenantId: string,
  promotionCodeId: string
) => {
  const { platformGateway } = useGateways();
  const queryClient = useQueryClient();

  return useQuery<PromotionCodeDto, HttpError>(
    [PltCacheKeys.TenantPromotionCodes, tenantId, promotionCodeId],
    () => platformGateway.getTenantPromotionCode(tenantId, promotionCodeId),
    {
      onSuccess: (dto: PromotionCodeDto) => {
        cacheUpsertSingleItemInArray({
          queryClient,
          item: dto,
          queryKey: [PltCacheKeys.TenantPromotionCodes, tenantId]
        });
      }
    }
  );
};

// COMPONENTS
const invalidateComponentsQueries = async (queryClient: QueryClient) => {
  const queries = queryClient.getQueryCache().getAll();
  const componentsQueries = queries.filter(
    q =>
      q.queryKey.includes(PltCacheKeys.ComponentsLite) ||
      q.queryKey.includes(PltCacheKeys.Components)
  );

  setTimeout(async () => {
    await Promise.all(
      componentsQueries.map(query => queryClient.invalidateQueries(query))
    );
  }, 1000);
};

export const useComponentsDefs = (args?: GetComponentsDefArgs) => {
  const { platformGateway } = useGateways();
  return useQuery<ComponentDefDto[], HttpError>(
    [PltCacheKeys.ComponentsDefs, args],
    () => platformGateway.getComponentsDefs(args),
    {
      cacheTime: Infinity,
      staleTime: Infinity
    }
  );
};

export const useComponents = (args?: GetComponentsArgs) => {
  const { platformGateway } = useGateways();
  return useQuery<ComponentLiteDto[], HttpError>(
    [PltCacheKeys.ComponentsLite, args],
    () => platformGateway.getComponents(args)
  );
};

export const useComponent = (args?: GetComponentArgs) => {
  const { platformGateway } = useGateways();
  return useQuery<ComponentDto, HttpError>(
    [PltCacheKeys.Components, args?.tenantId, args],
    () => platformGateway.getComponent(args)
  );
};

export const useAddOrUpdateComponent = (
  message: string,
  options?: Omit<
    UseMutationOptions<ComponentDto, HttpError, ComponentActionPayload>,
    "mutationFn"
  >
) => {
  const { platformGateway } = useGateways();
  const { success } = useFeedbackContext();
  const queryClient = useQueryClient();

  return useMutation<ComponentDto, HttpError, CreateComponentPayload>(
    platformGateway.addOrUpdateComponent,
    {
      onSuccess: async () => {
        await invalidateComponentsQueries(queryClient);
        success(message);
      },
      ...options
    }
  );
};

export const useInitializeComponent = (
  options?: Omit<
    UseMutationOptions<ComponentDto, HttpError, ComponentActionPayload>,
    "mutationFn"
  >
) => {
  const { platformGateway } = useGateways();
  const { success } = useFeedbackContext();
  const queryClient = useQueryClient();

  return useMutation<ComponentDto, HttpError, ComponentActionPayload>(
    platformGateway.initializeComponent,
    {
      onSuccess: async () => {
        await invalidateComponentsQueries(queryClient);
        success("Component initialized");
      },
      ...options
    }
  );
};

export const useDeInitializeComponent = () => {
  const { platformGateway } = useGateways();
  const { success } = useFeedbackContext();
  const queryClient = useQueryClient();

  return useMutation<ComponentDto, HttpError, ComponentActionPayload>(
    platformGateway.deInitializeComponent,
    {
      onSuccess: async () => {
        await invalidateComponentsQueries(queryClient);
        success("Component de-initialized");
      }
    }
  );
};

export const useArchiveComponent = () => {
  const { platformGateway } = useGateways();
  const { success } = useFeedbackContext();
  const queryClient = useQueryClient();

  return useMutation<ComponentDto, HttpError, ComponentActionPayload>(
    platformGateway.archiveComponent,
    {
      onSuccess: async () => {
        await invalidateComponentsQueries(queryClient);
        success("Component archived");
      }
    }
  );
};

export const useReConfigureComponent = () => {
  const { platformGateway } = useGateways();
  const { success } = useFeedbackContext();
  const queryClient = useQueryClient();

  return useMutation<ComponentDto, HttpError, ComponentActionPayload>(
    platformGateway.archiveComponent,
    {
      onSuccess: async () => {
        await invalidateComponentsQueries(queryClient);
        success("Component re-configured");
      }
    }
  );
};

export const useDisableComponent = () => {
  const { platformGateway } = useGateways();
  const { success } = useFeedbackContext();
  const queryClient = useQueryClient();

  return useMutation<ComponentDto, HttpError, ComponentActionPayload>(
    platformGateway.disableComponent,
    {
      onSuccess: async () => {
        await invalidateComponentsQueries(queryClient);
        success("Component disabled");
      }
    }
  );
};

export const useComponentActions = (args: GetComponentActionsArgs) => {
  const { platformGateway } = useGateways();
  return useQuery<ComponentActionDto[], HttpError>(
    [PltCacheKeys.ComponentActions, args.componentId],
    () => platformGateway.getComponentActions(args)
  );
};

export const useComponentSettings = (args: ComponentActionPayload) => {
  const { platformGateway } = useGateways();
  return useQuery<ComponentSettingsDto, HttpError>(
    [PltCacheKeys.ComponentSettings, args],
    () => platformGateway.getComponentSettings(args)
  );
};

export const useComponentSetting = (args: GetComponentSettingArgs) => {
  const { platformGateway } = useGateways();
  return useQuery<ComponentSettingDto, HttpError>(
    [PltCacheKeys.ComponentSetting, args],
    () => platformGateway.getComponentSetting(args)
  );
};

export const useCreateComponentSetting = (args: ComponentActionPayload) => {
  const { platformGateway } = useGateways();
  const { success } = useFeedbackContext();
  const queryClient = useQueryClient();

  return useMutation<
    Omit<ComponentDto, "implementations" | "id" | "eTag" | "changeLog">,
    HttpError,
    CreateSettingPayload
  >(payload => platformGateway.createComponentSettings(args, payload), {
    onSuccess: async () => {
      await invalidateComponentsQueries(queryClient);
      success("Component updated");
    }
  });
};

export const useUpdateComponentSetting = (args: UpdateSettingArgs) => {
  const { platformGateway } = useGateways();
  const { success } = useFeedbackContext();
  const queryClient = useQueryClient();

  return useMutation<ComponentSettingDto, HttpError, UpdateSettingPayload>(
    payload => platformGateway.updateComponentSettings(args, payload),
    {
      onSuccess: async () => {
        await invalidateComponentsQueries(queryClient);
        success("Component updated");
      }
    }
  );
};

export const useComponentsRequests = (
  args: GetComponentsRequestsArgs,
  options?: Omit<
    UseQueryOptions<ComponentRequestDto[], HttpError>,
    "queryKey" | "queryFn"
  >
) => {
  const { platformGateway } = useGateways();
  return useQuery<ComponentRequestDto[], HttpError>(
    [PltCacheKeys.ComponentActionsRequests, args],
    () => platformGateway.getComponentsRequests(args),
    options
  );
};

export const useRetryComponentRequest = () => {
  const { platformGateway } = useGateways();
  const { success } = useFeedbackContext();
  const queryClient = useQueryClient();

  return useMutation<ComponentRequestDto, HttpError, string>(
    platformGateway.retryComponentRequest,
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries([
          PltCacheKeys.ComponentActionsRequests
        ]);
        success("Component action request retry submitted");
      }
    }
  );
};

export const useComponentWorkflowRunInstance = (id: string) => {
  const { platformGateway } = useGateways();
  return useQuery<ComponentWorkflowRunInstanceDto, HttpError>(
    [PltCacheKeys.ComponentWorkflowRunInstance, id],
    async () => {
      try {
        return await platformGateway.getComponentWorkflowRunInstance(id);
      } catch (e) {
        if ((e as HttpError).httpStatusCode === HttpStatusCode.NotFound)
          return { workflowInstanceUrl: undefined };
        throw e;
      }
    }
  );
};

export const useComponentManagerWorkflowRunInstance = (id: string) => {
  const { platformGateway } = useGateways();
  return useQuery<ComponentWorkflowRunInstanceDto, HttpError>(
    [PltCacheKeys.ComponentManagerWorkflowRunInstance, id],
    async () => {
      try {
        return await platformGateway.getComponentManagerWorkflowRunInstance(id);
      } catch (e) {
        if ((e as HttpError).httpStatusCode === HttpStatusCode.NotFound)
          return { workflowInstanceUrl: undefined };
        throw e;
      }
    }
  );
};

export const useTenantTagQuery = (tenantId: string) => {
  const { platformGateway } = useGateways();
  return useQuery<TenantTag[], HttpError>(
    [PltCacheKeys.TenantTags, tenantId],
    () => platformGateway.getTenantTags(tenantId)
  );
};

export const useAddTenantTag = () => {
  const { platformGateway } = useGateways();
  const { success } = useFeedbackContext();
  const queryClient = useQueryClient();

  return useMutation<TenantTag, HttpError, NewTenantTag>(
    platformGateway.addTenantTag,
    {
      onSuccess: async dto => {
        await Promise.all([
          queryClient.invalidateQueries([
            PltCacheKeys.TenantTags,
            dto.tenantId
          ]),
          queryClient.invalidateQueries([PltCacheKeys.Tenants]),
          queryClient.invalidateQueries([PltCacheKeys.UsedTenantTags])
        ]);
        success("A new tag has been added successfully!");
      }
    }
  );
};

export const useDeleteTenantTag = () => {
  const { platformGateway } = useGateways();
  const { success } = useFeedbackContext();
  const queryClient = useQueryClient();

  return useMutation<TenantTag, HttpError, TenantTag>(
    platformGateway.deleteTenantTag,
    {
      onSuccess: async dto => {
        await Promise.all([
          queryClient.invalidateQueries([
            PltCacheKeys.TenantTags,
            dto.tenantId
          ]),
          queryClient.invalidateQueries([PltCacheKeys.Tenants]),
          queryClient.invalidateQueries([PltCacheKeys.UsedTenantTags])
        ]);
        success("A tag has been deleted successfully!");
      }
    }
  );
};

export const useUsedTenantTagsQuery = () => {
  const { platformGateway } = useGateways();
  return useQuery<UsedTenantTag[], HttpError>(
    [PltCacheKeys.UsedTenantTags],
    () => platformGateway.getUsedTenantTags()
  );
};

export const useRestrictionTypesRefData = () => {
  const { platformGateway } = useGateways();

  return useQuery<RefDataDto[], HttpError>(
    [PltCacheKeys.RestrictionTypesRefData],
    platformGateway.getRestrictionTypeRefData,
    {
      cacheTime: Infinity,
      staleTime: Infinity
    }
  );
};
